import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

type ScheduleInfo = {
  wo_number: string;
  source_order_number: string | null;
  status: string;
  client_name: string | null;
  product_name: string | null;
  fulfillment_method: string | null;
  preferred_delivery_date: string | null;
  preferred_delivery_time: string | null;
  proposed_delivery_date: string | null;
  proposed_delivery_time: string | null;
  schedule_note: string | null;
  destination_address: string | null;
  destination_city: string | null;
  destination_state: string | null;
  destination_zip: string | null;
};

function formatWhen(date: string | null, time: string | null) {
  if (!date) return "TBD";
  try {
    const d = new Date(`${date}T12:00:00`);
    const part = d.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return time ? `${part} · ${time}` : part;
  } catch {
    return time ? `${date} · ${time}` : date;
  }
}

export default function CustomerSchedulePage() {
  const { token } = useParams();
  const [info, setInfo] = useState<ScheduleInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<"view" | "reschedule" | "done">("view");
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [prefDate, setPrefDate] = useState("");
  const [prefTime, setPrefTime] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await fetch(`/api/public/schedule/${token}`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || "Link not found");
        setInfo(data);
      } catch (e: any) {
        setError(e?.message || "Unable to load schedule");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  async function approve() {
    if (!token) return;
    setBusy(true);
    try {
      const res = await fetch(`/api/public/schedule/${token}/approve`, { method: "POST" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Could not approve");
      setMessage("Thank you — your schedule is confirmed.");
      setMode("done");
    } catch (e: any) {
      setError(e?.message || "Failed");
    } finally {
      setBusy(false);
    }
  }

  async function requestReschedule() {
    if (!token) return;
    setBusy(true);
    try {
      const res = await fetch(`/api/public/schedule/${token}/reschedule`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reason,
          preferred_delivery_date: prefDate || null,
          preferred_delivery_time: prefTime || null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Could not send request");
      setMessage("Got it — our team will confirm a new window shortly.");
      setMode("done");
    } catch (e: any) {
      setError(e?.message || "Failed");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f4f6f4] flex items-center justify-center text-gray-600">
        Loading…
      </div>
    );
  }

  if (error && !info) {
    return (
      <div className="min-h-screen bg-[#f4f6f4] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-gray-200 p-6 text-center">
          <h1 className="text-xl font-bold text-gray-900">Schedule link</h1>
          <p className="mt-2 text-gray-600">{error}</p>
          <p className="mt-4 text-sm text-gray-500">Call (602) 637-0032 if you need help.</p>
        </div>
      </div>
    );
  }

  if (!info) return null;

  const isProposed = info.status === "reschedule_proposed";
  const when = isProposed
    ? formatWhen(info.proposed_delivery_date, info.proposed_delivery_time)
    : formatWhen(info.preferred_delivery_date, info.preferred_delivery_time);
  const addr = [info.destination_address, info.destination_city, info.destination_state, info.destination_zip]
    .filter(Boolean)
    .join(", ");
  const isPickup = info.fulfillment_method === "pickup";

  return (
    <div className="min-h-screen bg-[#f4f6f4] flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-br from-[#264027] to-[#3a5a3c] px-6 py-5 text-white">
          <div className="text-[11px] uppercase tracking-wider text-white/70 font-semibold">Soil Seed & Water</div>
          <h1 className="mt-1 text-xl font-bold">
            {mode === "done" ? "Thank you" : isProposed ? "Confirm new window" : "Your schedule"}
          </h1>
          <p className="text-sm text-white/85 mt-1">{info.source_order_number || info.wo_number}</p>
        </div>

        <div className="p-6 space-y-4">
          {error && <p className="text-sm text-red-600">{error}</p>}

          {mode === "done" ? (
            <p className="text-gray-800">{message}</p>
          ) : mode === "reschedule" ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">Tell us what works better. We'll confirm shortly.</p>
              <label className="block text-sm font-medium">
                Preferred date
                <input type="date" value={prefDate} onChange={(e) => setPrefDate(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" />
              </label>
              <label className="block text-sm font-medium">
                Preferred window
                <input type="text" value={prefTime} onChange={(e) => setPrefTime(e.target.value)} placeholder="e.g. mornings" className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" />
              </label>
              <label className="block text-sm font-medium">
                Reason (optional)
                <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" />
              </label>
              <div className="flex gap-2 pt-2">
                <button onClick={() => setMode("view")} className="flex-1 rounded-lg border border-gray-300 py-2.5 text-sm font-medium">Back</button>
                <button disabled={busy} onClick={requestReschedule} className="flex-1 rounded-lg bg-[#264027] text-white py-2.5 text-sm font-semibold disabled:opacity-50">
                  {busy ? "Sending…" : "Request reschedule"}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold">When</div>
                <div className="text-2xl font-bold text-gray-900 mt-1">{when}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold">
                  {isPickup ? "Pickup" : "Delivery"}
                </div>
                <div className="text-sm text-gray-800 mt-1">
                  {isPickup ? "1634 N 19th Ave, Phoenix, AZ 85009" : addr || "Address on file"}
                </div>
              </div>
              {info.product_name && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Order</div>
                  <div className="text-sm text-gray-800 mt-1">{info.product_name}</div>
                </div>
              )}
              {info.schedule_note && (
                <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">{info.schedule_note}</p>
              )}

              <div className="flex flex-col gap-2 pt-2">
                {isProposed && (
                  <button
                    disabled={busy}
                    onClick={approve}
                    className="w-full rounded-lg bg-[#264027] text-white py-3 text-sm font-semibold disabled:opacity-50"
                  >
                    {busy ? "Confirming…" : "Approve this window"}
                  </button>
                )}
                <button
                  onClick={() => setMode("reschedule")}
                  className="w-full rounded-lg border border-gray-300 py-3 text-sm font-medium text-gray-800"
                >
                  Request a different time
                </button>
              </div>
            </>
          )}

          <p className="text-xs text-center text-gray-400 pt-2">Questions? (602) 637-0032</p>
        </div>
      </div>
    </div>
  );
}
